import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { Dropdown, Link } from '@fluentui/react';
import { WIZARD_ACTIONS as SHARED_WIZARD_ACTIONS } from '@bingads-webui-campaign-react/conversion-wizard/src/conversion-wizard-reducer';
import { CodeSnippet, SeeExample, getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { StepContainer } from '../step-container/step-container';
import { GoalCreationWizardContext } from '../../goal-creation-wizard-context';
import { GtmSetupInstructionsView } from './gtm-instruction-view/gtm-setup-instructions';
import { CopyDownloadTag } from '../components/copy-download-tag';
import { InsertInstruction } from '../components/insert-instructions';
import { eventTrackingMethods, eventTagSetupTemplates, tagSetupExampleTemplates } from '../../shared/contants';
import { getEventTagModel } from '../utils/tag-generator';
import { EventParamSetting } from '../../components/event-param-setting';
import { validateSpecificView } from '../../shared/validation-util';
import { getErrorMessage, processPayload, saveGoal } from '../../conversion-goals-repository';
import { endActivity } from '../../shared/util';
import { WIZARD_ACTIONS } from '../../goal-creation-wizard-reducer';

const TagSetupInstallEventTagUnstyled = (props) => {
  const {
    classes,
    permissions,
  } = props;

  const wizardContext = useContext(GoalCreationWizardContext);
  const { logActivity, goalContext: { Type: goalType } } = wizardContext;
  const i18n = useContext(I18nContext);

  const [showEventGoalError, setShowEventGoalError] = useState(false);
  const resetShowEventGoalError = useCallback(() => setShowEventGoalError(false), []);

  const [goalObj, setGoalObj] = useState(wizardContext.goalContext);
  const updateSpecificSettings = useCallback((updatedProps) => {
    setGoalObj({
      ...goalObj,
      ...updatedProps,
    });
  }, [goalObj]);

  const isClarityTagSetupAllowed = permissions && permissions.dynamic && permissions.dynamic.IsClarityTagSetupAllowed;

  const displayEventGoalParameters = isClarityTagSetupAllowed && goalType === 'EventGoal';

  const {
    Action,
    ActionOperator,
    Category,
    CategoryOperator,
    Label,
    LabelOperator,
    Value,
    ValueOperator,
  } = goalObj;
  const tagModel = getEventTagModel(goalObj);

  const hardCodedEventTrackingMethod = goalType !== 'ProductConversionGoal' &&
    goalType !== 'EventGoal' ? 'PageLoad' : null;
  const [eventTrackingMethod, setEventTrackingMethod] = useState(hardCodedEventTrackingMethod);
  const [notificationError, setNotificationError] = useState(null);
  useEffect(() => {
    let notificationId;
    if (!_.isEmpty(notificationError)) {
      notificationId = notificationApi.showWarning(notificationError);
    }
    return () => notificationId && notificationApi.dismiss(notificationId);
  }, [notificationError]);

  const [footerEnabled, setFooterEnabled] = useState(true);
  const goToGtmInstructionPage = useCallback(() => {
    wizardContext.dispatch({
      type: SHARED_WIZARD_ACTIONS.WIZARD_UPDATE_GTM_SESSION_CONTEXT,
      GTMContext: {
        workflowCompleted: true,
      },
    });
  }, [wizardContext]);

  const handleCancel = useCallback(() => {
    wizardContext.exitWizard();
  }, [wizardContext]);

  const handleNext = useCallback(() => {
    // TODO: update handle next
    if (displayEventGoalParameters) {
      if (!validateSpecificView(goalObj)) {
        setShowEventGoalError(true);
      } else {
        saveGoal(processPayload(goalObj, permissions), wizardContext).then(() => {
          endActivity(logActivity);
          wizardContext.dispatch({
            type: WIZARD_ACTIONS.NAVIGATE_TO_LAST_STEP,
            data: {
              ...goalObj,
            },
            switchToEdit: true,
          });
        })
          .catch((response) => {
            setFooterEnabled(true);
            setNotificationError(getErrorMessage(response, i18n));
          });
        setFooterEnabled(false);
      }
    } else {
      wizardContext.goToNext();
    }
  }, [displayEventGoalParameters, goalObj, permissions, wizardContext, logActivity, i18n]);

  const primaryBtnText = useMemo(() => {
    if (displayEventGoalParameters) {
      return i18n.getString(_TL_('Save and next'));
    }
    return i18n.getString(_TL_(('Next')));
  }, [displayEventGoalParameters, i18n]);

  const footerConfig = useMemo(() => ({
    buttons: [{
      type: 'Secondary',
      onClick: wizardContext.goBack,
      isEnabled: footerEnabled,
      text: i18n.getString(_TL_(('Back'))),
    },
    {
      type: 'Primary',
      onClick: handleNext,
      // TODO: update handle next
      isEnabled: footerEnabled && (!_.isEmpty(eventTrackingMethod) || (wizardContext.GTMContext && wizardContext.GTMContext.workflowCompleted)),
      text: primaryBtnText,
    },
    {
      type: 'Cancel',
      onClick: handleCancel,
      isEnabled: footerEnabled,
      text: i18n.getString(_TL_(('Cancel'))),
    }],
  }), [wizardContext.goBack, wizardContext.GTMContext, footerEnabled, i18n, handleNext, eventTrackingMethod, primaryBtnText, handleCancel]);

  const onChangeEventTrackingMethodCb = useCallback((e, selected) => {
    setEventTrackingMethod(selected.key);
  }, [setEventTrackingMethod]);

  const copyCallback = useCallback((res) => {
    if (res && res.error) {
      logActivity.error(JSON.stringify(res));
    }
    logActivity.trace('Base tag copy clicked');
  }, [logActivity]);

  const downloadCallback = useCallback((res) => {
    if (res && res.error) {
      logActivity.error(JSON.stringify(res));
    }
    logActivity.trace('Base tag download clicked');
  }, [logActivity]);

  let tagTemplate;
  if (eventTrackingMethod === eventTrackingMethods.InlineAction.key) {
    tagTemplate = goalObj.GoalValueType === 'VariantValue' ?
      wizardContext.tagTemplates.InlineEventTagWithRevenueInstructions : wizardContext.tagTemplates.InlineEventTagInstructions;
  } else if (eventTrackingMethod === eventTrackingMethods.PageLoad.key) {
    tagTemplate = goalObj.GoalValueType === 'VariantValue' ?
      wizardContext.tagTemplates.PageLoadEventTagWithRevenueInstructions : wizardContext.tagTemplates.PageLoadEventTagInstructions;
  }

  const generateEventTagInstructions = useMemo(() => {
    if (wizardContext.GTMContext && wizardContext.GTMContext.workflowCompleted) {
      logActivity.trace('[GoalCreationWizard][GtmBaseTagSetup] render gtm instructions');
      return (
        <div>
          <GtmSetupInstructionsView
            i18n={i18n}
            type={goalType}
            context={{
              gtmAccountId: wizardContext.GTMContext.GTMAccountId,
              gtmAccountName: wizardContext.GTMContext.GTMAccountName,
              gtmContainerName: wizardContext.GTMContext.GTMContainerName,
              eventParams: {
                Action,
                ActionOperator,
                Category,
                CategoryOperator,
                Label,
                LabelOperator,
                Value,
                ValueOperator,
              },
            }}
          />
        </div>
      );
    }

    const showManualEventTrackingInstructions = eventTrackingMethods.InlineAction.key === eventTrackingMethod
      || eventTrackingMethods.PageLoad.key === eventTrackingMethod;
    return (
      <div>
        {!hardCodedEventTrackingMethod &&
          <div className={classNames(classes.contentBlock, classes.section)}>
            <div className={classNames(classes.bodyText, classes.section)}>
              <ReactTemplate
                template={i18n.getString(_TL_('This page has instructions for setting up the event tag manually on your website. If you would like to see instructions for setting up the event tag on Google Tag Manager instead then {{a}}click here{{/a}}.'))}
                model={{
                  // eslint-disable-next-line jsx-a11y/anchor-is-valid
                  a: text => <Link onClick={goToGtmInstructionPage} role="button" tabIndex={0}>{text}</Link>,
                }}
              />
            </div>
            <div className={classes.instructionsHeader}>
              <b>{i18n.getString(_TL_('When do you want to track this event?'))}</b>
              <p>{i18n.getString(_TL_('We will give you instructions for setting up the event tag based on what you select.'))}</p>
            </div>
            <Dropdown
              className="event-tracking-dropdown"
              label={i18n.getString(_TL_(''))}
              ariaLabel={i18n.getString(_TL_('Event tag option'))}
              selectedKey={eventTrackingMethod}
              onChange={onChangeEventTrackingMethodCb}
              options={Object.values(eventTrackingMethods).map(method => ({ ...method, text: i18n.getString(method.text) }))}
              styles={{ dropdown: { width: 250 } }}
              placeholder={i18n.getString(_TL_('Please select an option'))}
            />
          </div>
        }

        {showManualEventTrackingInstructions &&
          <div className={classNames(classes.contentBlock)}>
            <div className={classes.section}>
              <InsertInstruction
                i18n={i18n}
                template={tagTemplate}
                logActivity={logActivity}
                wizardDispatch={wizardContext.dispatch}
                permissions={permissions}
              />
            </div>
            <div className={classes.section}>
              <CodeSnippet
                model={tagModel}
                codeTemplate={wizardContext.tagTemplates[eventTagSetupTemplates[eventTrackingMethod]].join('\n')}
              />
            </div>
            <div className={classes.section}>
              <CopyDownloadTag
                model={tagModel}
                type={eventTagSetupTemplates[eventTrackingMethod]}
                instructionsTemplate={tagTemplate.slice(0, -1)}
                onCopyCallback={copyCallback}
                onDownloadCallback={downloadCallback}
                tagTemplates={wizardContext.tagTemplates}
                logActivity={logActivity}
                wizardDispatch={wizardContext.dispatch}
                permissions={permissions}
              />
            </div>
            <SeeExample
              i18n={i18n}
              message={i18n.getString(_TL_('The UET tag should be pasted on every page of your website. The event tag is only needed on the page that has the action you want to track.'))}
              exampleTemplate={wizardContext.tagTemplates[tagSetupExampleTemplates[eventTrackingMethod]].join('\n')}
              model={tagModel}
            />
          </div>}
      </div>
    );
  }, [Action, ActionOperator, Category, CategoryOperator, Label, LabelOperator, Value, ValueOperator, classes.bodyText, classes.contentBlock, classes.instructionsHeader, classes.section, copyCallback, downloadCallback, eventTrackingMethod, goToGtmInstructionPage, goalType, hardCodedEventTrackingMethod, i18n, logActivity, onChangeEventTrackingMethodCb, permissions, tagModel, tagTemplate, wizardContext.GTMContext, wizardContext.dispatch, wizardContext.tagTemplates]);

  return (
    <StepContainer
      subContainer
      step="tag-setup-selection"
      footerConfig={footerConfig}
      title={i18n.getString(_TL_('Install event tag'))}
    >

      {displayEventGoalParameters &&
        <div className={classNames(classes.contentBlock, classes.section)}>
          <EventParamSetting
            goalObj={goalObj}
            updateGoalProps={updateSpecificSettings}
            showError={showEventGoalError}
            resetShowError={resetShowEventGoalError}
          />
        </div>}
      {generateEventTagInstructions}
    </StepContainer>

  );
};

TagSetupInstallEventTagUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  permissions: PropTypes.shape({}).isRequired,
};

export const TagSetupInstallEventTag = withPerfMarker(withDefaultStyles(TagSetupInstallEventTagUnstyled, getStepStyles));
